import React, { useState } from 'react';
import { STOCK_CATEGORIES, formatPrice } from '../../constants/stocks';

const IMPOSTOR_VALUE = 4; // 1 impostor token = $4

function StockTradeModal({ stock, priceData, holding, userData, onBuy, onSell, onClose, isProcessing }) {
  const [mode, setMode] = useState('buy'); // 'buy' or 'sell'
  const [amount, setAmount] = useState('');
  const [inputMode, setInputMode] = useState('shares'); // 'shares' or 'dollars'
  const [currency, setCurrency] = useState('crewmate'); // 'crewmate' or 'impostor'

  if (!priceData) return null;

  const price = priceData.price; // price in dollars
  const category = STOCK_CATEGORIES[stock.category];
  const isPositive = priceData.changePercent >= 0;

  const crewmateBalance = userData?.crewmateTokens || 0;
  const impostorBalance = userData?.imposterTokens || 0;

  const numAmount = parseFloat(amount) || 0;
  let shares, dollarCost;

  if (inputMode === 'shares') {
    shares = numAmount;
    dollarCost = parseFloat((shares * price).toFixed(4));
  } else {
    dollarCost = numAmount;
    shares = price > 0 ? parseFloat((dollarCost / price).toFixed(8)) : 0;
  }

  // Token cost depends on currency
  const tokenCost = currency === 'impostor'
    ? parseFloat((dollarCost / IMPOSTOR_VALUE).toFixed(4))
    : dollarCost;

  const currencyIcon = currency === 'impostor' ? '🔴' : '🔵';
  const currentBalance = currency === 'impostor' ? impostorBalance : crewmateBalance;
  const maxSellShares = holding ? holding.shares : 0;

  function handleSubmit() {
    if (shares <= 0) return;
    if (mode === 'buy') {
      onBuy(stock.id, shares, currency);
    } else {
      onSell(stock.id, shares, currency);
    }
  }

  function handleMaxSell() {
    if (mode === 'sell' && maxSellShares > 0) {
      setInputMode('shares');
      setAmount(maxSellShares.toString());
    }
  }

  // P&L for current holding
  let holdingPnL = null;
  if (holding && holding.shares > 0) {
    const currentValue = holding.shares * price;
    const pnl = currentValue - holding.costBasis;
    const pnlPercent = holding.costBasis > 0 ? ((pnl / holding.costBasis) * 100) : 0;
    holdingPnL = { pnl, pnlPercent, currentValue };
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="stock-trade-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>

        {/* Stock header */}
        <div className="stock-trade-header">
          <span className="stock-trade-icon">{stock.icon}</span>
          <div className="stock-trade-info">
            <h3 className="stock-trade-name">{stock.name}</h3>
            <span className="stock-trade-category" style={{ color: category?.color }}>
              {category?.label}
            </span>
          </div>
          <div className="stock-trade-price-section">
            <span className="stock-trade-price">{formatPrice(price)}</span>
            <span className={`stock-trade-change ${isPositive ? 'positive' : 'negative'}`}>
              {isPositive ? '+' : ''}{priceData.changePercent.toFixed(2)}%
            </span>
          </div>
        </div>

        <p className="stock-trade-description">{stock.description}</p>

        {/* Current holding info */}
        {holdingPnL && (
          <div className="stock-trade-holding-info">
            <div className="stock-holding-row">
              <span>Your shares:</span>
              <span>{holding.shares.toFixed(4)}</span>
            </div>
            <div className="stock-holding-row">
              <span>Avg buy price:</span>
              <span>{formatPrice(holding.avgPrice)}</span>
            </div>
            <div className="stock-holding-row">
              <span>Current value:</span>
              <span>{formatPrice(holdingPnL.currentValue)}</span>
            </div>
            <div className="stock-holding-row">
              <span>P&L:</span>
              <span className={holdingPnL.pnl >= 0 ? 'positive' : 'negative'}>
                {holdingPnL.pnl >= 0 ? '+' : ''}${holdingPnL.pnl.toFixed(2)} ({holdingPnL.pnlPercent.toFixed(1)}%)
              </span>
            </div>
          </div>
        )}

        {/* Buy/Sell toggle */}
        <div className="stock-trade-toggle">
          <button
            className={`stock-toggle-btn ${mode === 'buy' ? 'active buy' : ''}`}
            onClick={() => setMode('buy')}
          >
            Buy
          </button>
          <button
            className={`stock-toggle-btn ${mode === 'sell' ? 'active sell' : ''}`}
            onClick={() => setMode('sell')}
            disabled={maxSellShares <= 0}
          >
            Sell
          </button>
        </div>

        {/* Currency selector */}
        <div className="stock-currency-toggle">
          <button
            className={`stock-currency-btn ${currency === 'crewmate' ? 'active' : ''}`}
            onClick={() => setCurrency('crewmate')}
          >
            🔵 Crewmate <span className="stock-currency-rate">($1 each)</span>
          </button>
          <button
            className={`stock-currency-btn ${currency === 'impostor' ? 'active' : ''}`}
            onClick={() => setCurrency('impostor')}
          >
            🔴 Impostor <span className="stock-currency-rate">($4 each)</span>
          </button>
        </div>
        <div className="stock-trade-balance">
          Balance: {currentBalance.toFixed(2)} {currencyIcon}
        </div>

        {/* Input mode toggle */}
        <div className="stock-input-mode-toggle">
          <button
            className={`stock-input-mode-btn ${inputMode === 'shares' ? 'active' : ''}`}
            onClick={() => setInputMode('shares')}
          >
            By Shares
          </button>
          <button
            className={`stock-input-mode-btn ${inputMode === 'dollars' ? 'active' : ''}`}
            onClick={() => setInputMode('dollars')}
          >
            By Dollars
          </button>
        </div>

        {/* Amount input */}
        <div className="stock-trade-input-section">
          <label className="stock-trade-label">
            {inputMode === 'shares' ? 'Number of shares' : 'Amount in dollars ($)'}
          </label>
          <div className="stock-trade-input-wrapper">
            <input
              type="number"
              className="stock-trade-input"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder={inputMode === 'shares' ? '0.0000' : '0.00'}
              min="0"
              step={inputMode === 'shares' ? '0.0001' : '0.01'}
            />
            {mode === 'sell' && maxSellShares > 0 && (
              <button className="stock-max-btn" onClick={handleMaxSell}>MAX</button>
            )}
          </div>
        </div>

        {/* Cost/proceeds preview */}
        <div className="stock-trade-preview">
          {mode === 'buy' ? (
            <>
              <div className="stock-preview-row">
                <span>Shares to buy:</span>
                <span>{shares > 0 ? shares.toFixed(4) : '—'}</span>
              </div>
              <div className="stock-preview-row">
                <span>Dollar cost:</span>
                <span>{dollarCost > 0 ? `$${dollarCost.toFixed(2)}` : '—'}</span>
              </div>
              <div className="stock-preview-row total">
                <span>Token cost:</span>
                <span>{tokenCost > 0 ? `${tokenCost.toFixed(2)} ${currencyIcon}` : '—'}</span>
              </div>
            </>
          ) : (
            <>
              <div className="stock-preview-row">
                <span>Shares to sell:</span>
                <span>{shares > 0 ? shares.toFixed(4) : '—'}</span>
              </div>
              <div className="stock-preview-row">
                <span>Dollar value:</span>
                <span>{dollarCost > 0 ? `$${dollarCost.toFixed(2)}` : '—'}</span>
              </div>
              <div className="stock-preview-row total">
                <span>You receive:</span>
                <span>{tokenCost > 0 ? `${tokenCost.toFixed(2)} ${currencyIcon}` : '—'}</span>
              </div>
              {holding && shares > 0 && (
                <div className="stock-preview-row">
                  <span>Est. profit:</span>
                  <span className={(dollarCost - (shares / holding.shares) * holding.costBasis) >= 0 ? 'positive' : 'negative'}>
                    ${((dollarCost - (shares / holding.shares) * holding.costBasis)).toFixed(2)}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Submit button */}
        <button
          className={`stock-trade-submit ${mode === 'buy' ? 'buy' : 'sell'}`}
          onClick={handleSubmit}
          disabled={isProcessing || shares <= 0 || (mode === 'buy' && tokenCost > currentBalance) || (mode === 'sell' && shares > maxSellShares)}
        >
          {isProcessing ? 'Processing...' : mode === 'buy' ? `Buy ${stock.name}` : `Sell ${stock.name}`}
        </button>

        {mode === 'buy' && tokenCost > currentBalance && tokenCost > 0 && (
          <p className="stock-trade-error">Insufficient {currency} tokens (have {currentBalance.toFixed(2)} {currencyIcon})</p>
        )}
        {mode === 'sell' && shares > maxSellShares && shares > 0 && (
          <p className="stock-trade-error">Insufficient shares (have {maxSellShares.toFixed(4)})</p>
        )}
      </div>
    </div>
  );
}

export default StockTradeModal;
